/**
 * The chat's tab-scoped persistence, and who it belongs to (CASA 6.6.1).
 *
 * `useStreamChat` keeps the current conversation id and its completed messages
 * in sessionStorage so a page refresh is not jarring. That state belongs to the
 * signed-in person, and sessionStorage outlives a sign-out, a redirect's reload,
 * and a sign-out done in another tab. So:
 *
 * - AuthContext tells this module when a session starts (`startChatSession`,
 *   with the user id) and ends (`endChatSession`). Each change opens a new
 *   epoch; a chat hook remembers the epoch it mounted in.
 * - Every write names that epoch and is dropped unless it is still the current
 *   one with someone signed in, so work that finishes after the sign-out (a
 *   streamed chunk, a conversation fetch, an abort's own finalize) cannot put
 *   the signed-out user's chat back.
 * - Writes record the owner; a read returns the chat only to that same user and
 *   discards anyone else's.
 * - `endChatSession` also aborts the chat's in-flight work (streams and
 *   conversation fetches register an abort here).
 */

import type { ChatMessage } from '@/types/action';

export const CONV_STORAGE_KEY = 'askwingo-conversation-id';
export const MSGS_STORAGE_KEY = 'askwingo-chat-messages';
export const OWNER_STORAGE_KEY = 'askwingo-chat-owner';

let owner: string | null = null;
let epoch = 0;
const inFlight = new Set<() => void>();

/** The epoch a chat hook mounts in; pass it to every write. */
export function chatSessionEpoch(): number {
  return epoch;
}

function removeAll(): void {
  try {
    sessionStorage.removeItem(CONV_STORAGE_KEY);
    sessionStorage.removeItem(MSGS_STORAGE_KEY);
    sessionStorage.removeItem(OWNER_STORAGE_KEY);
  } catch {
    /* storage blocked: nothing was stored, so nothing is left behind */
  }
}

/**
 * A session began for ``userId``. ``resume`` is a reload of that same session
 * (its chat may rehydrate); a fresh sign-in forgets whatever the tab still holds.
 */
export function startChatSession(userId: string, { resume }: { resume: boolean }): void {
  if (!resume) removeAll();
  owner = userId || null;
  epoch += 1;
}

/** The session ended: stop the chat's in-flight work and forget its chat. */
export function endChatSession(): void {
  owner = null;
  epoch += 1;
  for (const abort of Array.from(inFlight)) {
    try {
      abort();
    } catch {
      /* one failing abort must not keep the others running */
    }
  }
  removeAll();
}

/** Register an abort for work in flight; returns the unregister. */
export function registerChatAbort(abort: () => void): () => void {
  inFlight.add(abort);
  return () => {
    inFlight.delete(abort);
  };
}

function writable(writerEpoch: number): boolean {
  return owner !== null && writerEpoch === epoch;
}

function write(writerEpoch: number, key: string, value: string | null): void {
  if (!writable(writerEpoch)) return;
  try {
    sessionStorage.setItem(OWNER_STORAGE_KEY, owner as string);
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* storage blocked: the chat just won't survive a refresh */
  }
}

export function persistConversationId(writerEpoch: number, conversationId: string): void {
  write(writerEpoch, CONV_STORAGE_KEY, conversationId);
}

/** ``null`` forgets the stored messages (a new chat). */
export function persistMessages(writerEpoch: number, messages: ChatMessage[] | null): void {
  write(writerEpoch, MSGS_STORAGE_KEY, messages === null ? null : JSON.stringify(messages));
}

/** The signed-in user's own stored chat, or null; anyone else's is discarded. */
export function readChatSession(): { conversationId: string; messages: ChatMessage[] } | null {
  try {
    if (owner === null) return null;
    if (sessionStorage.getItem(OWNER_STORAGE_KEY) !== owner) {
      removeAll();
      return null;
    }
    const conversationId = sessionStorage.getItem(CONV_STORAGE_KEY);
    if (!conversationId) return null;
    return { conversationId, messages: readMessages() };
  } catch {
    return null; // blocked storage: start fresh
  }
}

function readMessages(): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(MSGS_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as ChatMessage[]) : [];
    return parsed.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }));
  } catch {
    return []; // corrupt messages: keep the conversation, start its view fresh
  }
}
